/**
 * PlaceholderResolverModal — intercalates between "COPIER" and the clipboard
 * write when a cas pratique prompt contains [BRACKETED] placeholders.
 *
 * Flow:
 *   1. User clicks "COPIER LE PROMPT" on a cas pratique card.
 *   2. CasPratiques.jsx detects placeholders → opens this modal (not the
 *      PromptCopiedModal).
 *   3. User fills one input per unique placeholder.
 *   4. On submit, we replace all occurrences in the prompt string, copy the
 *      resolved prompt to the clipboard, and the parent then opens the
 *      PromptCopiedModal with the resolved prompt.
 *
 * Why not a toast?
 *   - A toast is passive (user may miss it) and still requires manual
 *     search-and-replace in ChatGPT. The modal does it for them in 10s.
 */
import React, { useMemo, useState } from "react";
import { X, ClipboardCheck } from "lucide-react";
import { Button } from "@/components/ui/button";

// Human-readable labels + helpful hints per placeholder token.
// Keys MUST be UPPERCASE ASCII and match the token inside the brackets.
// (The parser regex is `/\[([A-Z_]+)\]/g` — accented or lowercase tokens
// are silently ignored, so every cas pratique MUST use the ASCII form.)
const PLACEHOLDER_META = {
  NIVEAU:     { label: "Niveau de classe",         hint: "ex : 4ème, Seconde, CM2, Terminale STMG" },
  TYPE:       { label: "Type de poste / profil",   hint: "ex : PACD, APFL, directeur adjoint" },
  X:          { label: "Académie / zone",          hint: "ex : Versailles, Créteil, Grenoble" },
  SUJET:      { label: "Sujet imposé",             hint: "ex : Le travail dans la philosophie des Lumières" },
  DISCIPLINE: { label: "Discipline",               hint: "ex : lettres modernes, mathématiques, SVT" },
  NOTION:     { label: "Notion / concept",         hint: "ex : la démocratie, la photosynthèse" },
  MATIERE:    { label: "Matière",                  hint: "ex : histoire, physique-chimie" },
  // Added 04 Oct 2026 — audit found these were being used raw in cas
  // pratiques without any UI metadata, falling back to the token itself.
  THEME:      { label: "Thème / sujet",            hint: "ex : numérique à l'école, décrochage scolaire, écoanxiété" },
  PRENOM:     { label: "Prénom de l'élève",        hint: "ex : Léa, Nathan, Youssef" },
  NOTE:       { label: "Note / moyenne (sur 20)",  hint: "ex : 12.5, 8, 15" },
  DATE:       { label: "Date",                     hint: "ex : 15 janvier 2026, lundi 3 mars" },
  MOTIF:      { label: "Motif / raison",           hint: "ex : rendez-vous médical, séjour familial" },
  EFFECTIF:   { label: "Effectif de la classe",    hint: "ex : 28 élèves, 32 élèves" },
  TROUBLE:    { label: "Trouble ou difficulté",    hint: "ex : dyslexie, dyscalculie, TDAH, dysphasie" },
  SITUATION:  { label: "Situation à décrire",      hint: "ex : élève perturbateur, conflit entre deux camarades" },
  REVUE:      { label: "Revue professionnelle",    hint: "ex : Cahiers Pédagogiques, Revue EP&S, Les Cahiers du CEDRE" },
};

// Only matches ASCII uppercase + underscore inside brackets — sufficient for
// all our placeholders (NIVEAU, TYPE, X, SUJET, DISCIPLINE, NOTION, MATIERE).
// Keeping ASCII avoids any UTF-8 round-trip surprises in the bundler.
const BRACKET_RE = /\[([A-Z_]+)\]/g;

/** Extract unique placeholder tokens from a prompt string. */
export function extractPlaceholders(prompt) {
  if (!prompt) return [];
  // Build a FRESH RegExp each call to avoid the well-known `/g`+`lastIndex`
  // stateful trap. `matchAll` returns an iterator so no manual state mgmt.
  const re = new RegExp(BRACKET_RE.source, "g");
  const seen = new Set();
  const out = [];
  for (const m of prompt.matchAll(re)) {
    const token = m[1];
    if (!seen.has(token)) {
      seen.add(token);
      out.push(token);
    }
  }
  return out;
}

/** Replace every occurrence of [TOKEN] with the user-provided value. */
export function resolvePlaceholders(prompt, values) {
  if (!prompt) return "";
  const re = new RegExp(BRACKET_RE.source, "g");
  return prompt.replace(re, (match, token) => {
    const v = (values[token] || "").trim();
    return v || match; // keep the bracketed token if left empty — honest signal
  });
}

export function PlaceholderResolverModal({ cas, onCancel, onResolved }) {
  const placeholders = useMemo(() => extractPlaceholders(cas?.prompt), [cas]);
  const [values, setValues] = useState(() =>
    Object.fromEntries(placeholders.map((p) => [p, ""]))
  );

  if (!cas || placeholders.length === 0) return null;

  const resolved = resolvePlaceholders(cas.prompt, values);
  const allFilled = placeholders.every((p) => (values[p] || "").trim().length > 0);

  const handleSubmit = (e) => {
    e?.preventDefault?.();
    try {
      navigator.clipboard.writeText(resolved).catch(() => {});
    } catch (_) {}
    onResolved(resolved, values);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 overflow-y-auto"
      onClick={onCancel}
      data-testid="placeholder-resolver-modal"
    >
      <div
        className="bg-background border border-border w-full max-w-xl my-8 flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 border-b border-border">
          <div className="min-w-0 pr-3">
            <div className="sux-marker text-[#002FA7] mb-1">Personnalisez votre prompt</div>
            <div className="font-mono text-[11px] uppercase tracking-[0.15em] text-muted-foreground truncate">
              Cas #{String(cas.id).padStart(2, "0")} — {cas.title}
            </div>
          </div>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Fermer"
            data-testid="placeholder-modal-close"
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-4">
          <div className="font-['IBM_Plex_Sans'] text-sm text-muted-foreground">
            Ce prompt contient <b>{placeholders.length}</b>{" "}
            {placeholders.length === 1 ? "variable à personnaliser" : "variables à personnaliser"}.
            Remplissez-les ci-dessous, SUX1 copiera le prompt déjà prêt dans
            votre presse-papier.
          </div>

          {placeholders.map((token) => {
            const meta = PLACEHOLDER_META[token] || { label: token, hint: "" };
            return (
              <label key={token} className="flex flex-col gap-1.5">
                <span className="font-mono uppercase tracking-[0.14em] text-[11px] text-foreground">
                  {meta.label}{" "}
                  <span className="text-muted-foreground">
                    (remplace <code className="font-mono">[{token}]</code>)
                  </span>
                </span>
                <input
                  type="text"
                  value={values[token]}
                  onChange={(e) => setValues({ ...values, [token]: e.target.value })}
                  placeholder={meta.hint}
                  data-testid={`placeholder-input-${token}`}
                  autoFocus={token === placeholders[0]}
                  className="w-full bg-background border border-border p-2.5 font-sans text-sm focus:outline-none focus:border-[#002FA7]"
                />
              </label>
            );
          })}

          {/* Live preview */}
          <div className="mt-2 border border-[#002FA7]/30 bg-[#002FA7]/[0.04]">
            <div className="px-3 py-1.5 border-b border-[#002FA7]/30 flex items-center justify-between">
              <span className="font-mono uppercase tracking-[0.14em] text-[10px] text-[#002FA7]">
                Aperçu du prompt final
              </span>
            </div>
            <pre
              data-testid="placeholder-preview"
              className="px-3 py-2.5 font-['IBM_Plex_Sans'] text-[13px] leading-relaxed whitespace-pre-wrap break-words max-h-48 overflow-y-auto"
            >
              {resolved}
            </pre>
          </div>
        </form>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-border flex items-center justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            data-testid="placeholder-cancel-btn"
            className="font-mono uppercase tracking-[0.14em] text-[11px]"
          >
            Annuler
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={!allFilled}
            data-testid="placeholder-submit-btn"
            className="bg-[#002FA7] hover:bg-[#001a66] text-white font-mono uppercase tracking-[0.14em] text-[11px] disabled:opacity-50"
          >
            <ClipboardCheck className="w-4 h-4 mr-1.5" />
            Copier le prompt personnalisé
          </Button>
        </div>
      </div>
    </div>
  );
}

export default PlaceholderResolverModal;
